import {Injectable} from '@angular/core';

import {Observable} from 'rxjs/Observable';
import {timer} from 'rxjs/observable/timer';
import {Subscription} from 'rxjs/Subscription';
import 'rxjs/add/operator/map';

import {environment} from '../../../environments/environment';

import {HttpClient, HttpHeaders} from '@angular/common/http';

import {DataService} from './data.service';
import {SocketService} from './socket.service';
import {UserService} from './user.service';
import {MessageService} from './message.service';
import {OfferEntryDataService} from './offer-entry-data.service';
import {UtilsService} from './utils.service';

@Injectable()
export class AuthenticationService {

    timer: any;
    timerSubscription: Subscription;

    constructor(
        private http: HttpClient,
        private data: DataService,
        private socketService: SocketService,
        private userService: UserService,
        private messageService: MessageService,
        private offerEntryDataSerivce: OfferEntryDataService,
        private utilsService: UtilsService
    ) {
        const currentUser = JSON.parse(sessionStorage.getItem('currentUser'));
        if (currentUser) {
            this.timer = timer(1);  // schedule refresh immediately
            this.timerSubscription = this.timer.subscribe((t) => this.refreshToken());
        }
    }

    initializeApp() {
        this.userService.init();
        this.messageService.init();
        this.offerEntryDataSerivce.init();
    }

    public ngOnDestroy() {
        if (this.timerSubscription) {
            this.timerSubscription.unsubscribe();
            this.timerSubscription = null;
        }
    }

    login(username: string, password: string, locale: string = 'en'): Observable<any> {
        const clientId = environment.common.client_id;
        const body = {
            grant_type: 'password',
            username: username,
            password: password,
            client_id: clientId
        };

        return this.data.post<any>(environment.auth_api_url + 'authenticate', body)
            .map(data => {
                if (data && data.access_token) {
                    this.initializeApp();
                    this.socketService.login(username);
                    // store user details and jwt token in local storage to keep user logged in between page refreshes
                    data.client_id = clientId;
                    data.username = username;
                    data.locale = locale;
                    let refreshMilliseconds = (parseFloat(data.expires_in) - (Date.now() / 1000)) * 1000;
                    refreshMilliseconds = refreshMilliseconds - 30000;  // allow 30 seconds earlier in case server is slow
                    if (this.timerSubscription) {
                        this.timerSubscription.unsubscribe();
                    }
                    this.timer = timer(refreshMilliseconds, refreshMilliseconds);  // schedule refresh
                    this.timerSubscription = this.timer.subscribe((t) => this.refreshToken());

                    this.setUser(data);
                }
            });
    }

    refreshToken(): void {
        this.utilsService.debugLog('refresh token execution');
        const currentUser = JSON.parse(sessionStorage.getItem('currentUser'));
        const refreshToken = currentUser.refresh_token;
        const clientId = currentUser.client_id;
        if (!refreshToken || !clientId) {
            console.error('Missing token or client!');
            console.error(currentUser);
            return;
        }

        const body = {
            grant_type: 'refresh_token',
            token: refreshToken,
            client_id: clientId
        };

        this.http.post<any>(environment.auth_api_url + 'authenticate', body).subscribe(refresh => {
            if (refresh && refresh.access_token) {
                // store user details and jwt token in local storage to keep user logged in between page refreshes
                refresh.refresh_token = refreshToken;
                refresh.client_id = currentUser.client_id;
                refresh.username = currentUser.username;
                refresh.locale = currentUser.locale;
                this.setUser(refresh);
                this.utilsService.debugLog('Access Token has been re-newed!');
                let refreshMilliseconds = (parseFloat(refresh.expires_in) - (Date.now() / 1000)) * 1000;
                refreshMilliseconds = refreshMilliseconds - 30000;  // allow 30 seconds earlier in case server is slow
                if (this.timerSubscription) {
                    this.timerSubscription.unsubscribe();
                }
                this.timer = timer(refreshMilliseconds, refreshMilliseconds);  // schedule refresh
                this.timerSubscription = this.timer.subscribe((t) => this.refreshToken());
                this.utilsService.debugLog('Will renew again in ' + refreshMilliseconds / 1000 + 'seconds');
            }
        });
    }

    logout(): Observable<boolean> {
        const currentProgram = sessionStorage.getItem('currentProgram');
        const currentUser = JSON.parse(sessionStorage.getItem('currentUser'));
        if (!currentUser) {
            console.error('Cannot logout, no current user');
            return;
        }
        const refreshToken = currentUser.refresh_token;
        const clientId = currentUser.client_id;
        if (!refreshToken || !clientId) {
            console.error('Cannot logout, missing token or client id');
            console.error(currentUser);
            return;
        }

        const body = {
            token: refreshToken,
            client_id: clientId
        };

        this.timerSubscription.unsubscribe(); // stop refreshing authentication token
        this.timerSubscription = null;

        return this.data.post<any>(environment.auth_api_url + 'logout', body)
            .map(res => {
                if (res) {
                    this.socketService.logout();
                    this.removeUser();
                    return true;
                }
                return false;
            }).catch(error => {
                this.utilsService.debugLog(error);
                this.removeUser();
                if (error.status !== 404 && error.status !== 401) {
                    this.setUser(currentUser, currentProgram);
                } else {
                    this.socketService.logout();
                }
                return Observable.throw(error);
            });
    }

    setUser(currentUser, currentProgram = null): void {
        sessionStorage.setItem('currentUser', JSON.stringify(currentUser));
        if (currentProgram) {
            this.setProgram(currentProgram);
        }
    }

    removeUser(): void {
        sessionStorage.removeItem('currentUser');
        this.removeProgram();
    }

    setProgram(currentProgram): void {
        sessionStorage.setItem('currentProgram', currentProgram);
    }

    removeProgram(): void {
        sessionStorage.removeItem('currentProgram');
    }
}



// WEBPACK FOOTER //
// ./src/app/core/services/authentication.service.ts